import { FC, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useGetUserListById } from "../../api/services/manageUser/manageUserApi";
import {
  ITabs,
  MuiModal,
  MuiTabPanel,
  MuiTabs,
  resolveContent,
} from "../../components";
import { ADMIN_ROLE_IDS, PAGE_IDS } from "../../helpers/constants";
import { getFullName } from "../../helpers/utils";
import { useSiteCore } from "../../hooks/useSiteCore";
import { useToast } from "../../hooks/useToast";
import { toggleLoader } from "../../redux/slices/appState/appStateSlice";
import {
  ADD_USER_TABS,
  CandidatePortalRole,
} from "../createUser/modelsAndSchemas/models";
import { createUserSiteCoreKeys } from "../createUser/siteCore/SiteCoreKeys";
import { ViewUserCoachDetail } from "./components/viewUserCoachDetails/ViewUserCoachDetails";
import ViewPowerBiReportDetails from "./components/viewUserPowerBiReports/ViewUserPowerBiReportDetails";
import { ViewUserRoleDetail } from "./components/viewUserRoleDetails/ViewUserRoleDetails";
import { ViewUserUserDetail } from "./components/viewUserUserDetails/ViewUserUserDetails";
import { ViewUserWrapper } from "./ViewUser.style";

export const pageId = PAGE_IDS.CREATE_USER;

interface ViewUserProps {
  open: boolean;
  staffId: string;
  onClose: () => void;
  handleEditUser: (staffId: string) => void;
}

export const ViewUser: FC<ViewUserProps> = ({
  open,
  staffId,
  onClose,
  handleEditUser,
}) => {
  const [selectedTab, setSelectedTab] = useState(ADD_USER_TABS.ROLE_DETAIL);
  const {
    isPending: isUserDataPending,
    data: userData,
    isError: isUserDataError,
    refetchWithParams: refetchUserData,
  } = useGetUserListById();

  const { commonLabels, useFetchLabel } = useSiteCore();
  const dispatch = useDispatch();
  const { displayToast } = useToast();
  useEffect(() => {
    if (staffId) {
      refetchUserData(staffId);
    }
  }, [staffId]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isUserDataPending,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isUserDataPending]);

  useEffect(() => {
    if (isUserDataError) {
      displayToast(commonLabels.someThingWentWrongPleaseTryAgainLater, "error");
      dispatch(toggleLoader({ isVisible: false }));
    }
  }, [isUserDataError]);

  const getUserLabel = useFetchLabel(createUserSiteCoreKeys.userDetailKey);

  const isCoachAdmin = useMemo(() => {
    const role = userData?.adminUserRoles?.candidatePortalRole?.map(
      (role: CandidatePortalRole) => role.roleId,
    );
    return (
      role?.includes(ADMIN_ROLE_IDS.COACH) ||
      role?.includes(ADMIN_ROLE_IDS.COACH_ADMIN) ||
      role?.includes(ADMIN_ROLE_IDS.RESUME_COACH) ||
      role?.includes(ADMIN_ROLE_IDS.LEAD_CONSULTANT) ||
      role?.includes(ADMIN_ROLE_IDS.DELIVERY_COACH) ||
      role?.includes(ADMIN_ROLE_IDS.TEMPORARY_COACH_HELPDESK)
    );
  }, [userData]);

  const tabIdRoleDetails = `${pageId}-tab1`;
  const tabIdUserDetails = `${pageId}-tab2`;
  const tabIdCoachDetails = `${pageId}-tab3`;
  const USER_TABS: ITabs[] = [
    {
      id: tabIdRoleDetails,
      label: getUserLabel(createUserSiteCoreKeys.roleDetailsTab),
      content: <ViewUserRoleDetail userData={userData} />,
    },
    {
      id: tabIdUserDetails,
      label: getUserLabel(createUserSiteCoreKeys.userDetailsTab),
      content: <ViewUserUserDetail userData={userData} />,
    },
    ...(isCoachAdmin
      ? [
          {
            id: tabIdCoachDetails,
            label: getUserLabel(createUserSiteCoreKeys.coachDetailsTab),
            content: <ViewUserCoachDetail userData={userData} />,
          },
        ]
      : []),
    ...(userData?.hasAccessToPowerBIReport
      ? [
          {
            id: getUserLabel(createUserSiteCoreKeys.powerBIReportsTab),
            label: getUserLabel(createUserSiteCoreKeys.powerBIReportsTab),
            content: (
              <ViewPowerBiReportDetails userData={userData} pageId={pageId} />
            ),
          },
        ]
      : []),
  ];

  const renderedTabs = USER_TABS.map((tab, i) => ({
    ...tab,
    testId: `${pageId}-tab${i + 1}`,
    index: i,
  }));

  const onTabChange = (event: React.ChangeEvent<{}>, value: number) => {
    setSelectedTab(value);
  };

  return (
    <MuiModal
      onClose={onClose}
      title={`${getUserLabel(createUserSiteCoreKeys.viewUserHeader)} - ${getFullName(
        userData?.firstName,
        userData?.lastName,
        true,
      )}`}
      open={open}
      tertiaryButtonTitle={commonLabels.edit}
      onTertiaryButtonClick={() => handleEditUser(staffId)}
      onSecondaryButtonClick={onClose}
      secondaryButtonTitle={commonLabels.close}
      hasLargeContent
      className="view-user-modal"
    >
      <ViewUserWrapper>
        <MuiTabs
          tabs={renderedTabs}
          value={selectedTab}
          onChange={onTabChange}
          tabsTestIds={renderedTabs.map((tab) => tab.id)}
        >
          {renderedTabs.map((tab, index) => (
            <MuiTabPanel
              key={tab.id}
              panelId={renderedTabs[selectedTab].id}
              value={selectedTab}
              index={index}
              isPanelWithMount
              ariaLabelledById={tab.id}
            >
              {resolveContent(tab.content)}
            </MuiTabPanel>
          ))}
        </MuiTabs>
      </ViewUserWrapper>
    </MuiModal>
  );
};
